import { useEffect, useState, useCallback, useMemo } from "react";
import { Combobox, Listbox, Icon, LegacyStack, Tag } from "@shopify/polaris";
import { SearchIcon } from "@shopify/polaris-icons";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { fetchUserByPermission } from "services/user-select.service";

interface UserSelectProps {
  value?: string;
  onChange: (selectedValue: string) => void;
  isRequired?: boolean;
  isDisable?: boolean;
  label?: string;
  Permission: string;
  error?: string;
}

interface User {
  id: string;
  name: string;
  email: string;
}

const UserSelectByPermissions: React.FC<UserSelectProps> = ({
  value,
  onChange,
  isRequired = false,
  isDisable = false,
  label = "select_sign_permission_user",
  Permission = "",
  error: propsErr = "",
}) => {
  const { t } = useTranslation();
  const [allOptions, setAllOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [selectedOptions, setSelectedOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const { companyId } = useParams<{ companyId: string }>();

  const displayOptions = useMemo(() => {
    const q = inputValue.trim().toLowerCase();
    if (!q) return allOptions;
    return allOptions.filter((o) => o.label.toLowerCase().includes(q));
  }, [allOptions, inputValue]);

  const handleChange = useCallback(
    (selectedValue: string) => {
      if (isRequired && !selectedValue) {
        setError(t("field_is_required"));
      } else {
        setError("");
      }
      onChange(selectedValue);
    },
    [isRequired, onChange, t]
  );

  useEffect(() => {
    async function fetchData() {
      if (!companyId) return;
      try {
        setIsLoading(true);
        const data = await fetchUserByPermission(companyId, Permission);
        const dataCollection = data.usersCollection.map((row: User) => ({
          label: row.name || row.email,
          value: row.id,
        }));
        setAllOptions(dataCollection);
        if (Permission === "Signature" && dataCollection.length > 0 && !value) {
          onChange(dataCollection[0].value);
        }
      } catch (e) {
        console.error("Error fetching user collection:", e);
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();
  }, [companyId, Permission]); // eslint-disable-line react-hooks/exhaustive-deps -- intentional: avoid re-firing on value

  useEffect(() => {
    if (!value) {
      setSelectedOptions([]);
      return;
    }
    const found = allOptions.find((o) => o.value === value);
    if (found) {
      setSelectedOptions([found]);
    }
  }, [value, allOptions]);

  const handleInputChange = useCallback((input: string) => {
    setInputValue(input);
  }, []);

  const handleSelection = useCallback(
    (selected: string) => {
      const found = displayOptions.find((option) => option.value === selected);
      if (found) {
        setSelectedOptions([found]);
        setInputValue("");
        handleChange(selected);
      }
    },
    [displayOptions, handleChange]
  );

  const handleTagRemove = useCallback(() => {
    setSelectedOptions([]);
    setInputValue("");
    handleChange("");
  }, [handleChange]);

  const optionsMarkup = displayOptions.map((option) => (
    <Listbox.Option
      key={option.value}
      value={option.value}
      selected={selectedOptions.some((o) => o.value === option.value)}
      accessibilityLabel={option.label}
    >
      {option.label}
    </Listbox.Option>
  ));

  const verticalContentMarkup =
    selectedOptions.length > 0 ? (
      <LegacyStack spacing="extraTight" alignment="center">
        <Tag
          {...(!isDisable ? { onRemove: handleTagRemove } : {})}
        >
          {selectedOptions[0].label}
        </Tag>
      </LegacyStack>
    ) : null;

  return (
    <div className="user-select">
      <Combobox
        allowMultiple={false}
        activator={
          <Combobox.TextField
            prefix={<Icon source={SearchIcon as any} tone="base" />}
            onChange={handleInputChange}
            label={t(label)}
            value={inputValue}
            placeholder={
              t("user_search") !== "user_search"
                ? t("user_search")
                : "Search user..."
            }
            autoComplete="off"
            disabled={isDisable}
            error={error || propsErr}
            requiredIndicator={isRequired}
            verticalContent={verticalContentMarkup}
          />
        }
      >
        <Listbox onSelect={handleSelection}>
          {isLoading ? (
            <Listbox.Loading accessibilityLabel="Loading" />
          ) : (
            <>{optionsMarkup}</>
          )}
        </Listbox>
      </Combobox>
    </div>
  );
};

export default UserSelectByPermissions;
